/**
 * Website-Texte + Bearbeitungsmodus der KAC-Website (AgoraHub, Plan docs/PUBLIC_WEBSITE_API_PLAN.md, Phase W4).
 *
 * 1. Veroeffentlichte Website-Texte (`website_texts.entries`, Public API) werden beim Start als i18next-Ressourcen
 *    eingespielt. Die Uebersetzungs-Schluessel SIND die Kennungen; jedes vorhandene `t()` zeigt so den gepflegten Text.
 *    Fehlt ein Wert in einer Sprache, gilt der eingebaute Text DIESER Sprache. Nur Kennungen der Freigabeliste
 *    (`editKeys.js`) werden angewendet.
 * 2. Ueber-uns-Texte kommen aus `about_us` (eine zentrale Wahrheit mit der App): `useAboutUs()`; der eingebaute Text
 *    (`about_us_builtin.<pfad>`) gilt nur, wenn das Feld in AgoraHub leer ist.
 * 3. Adapter fuer das Bearbeitungs-Skript (agorahub.de/website-editor/v1/editor.js): texts(), setText(), setSlot(),
 *    pause(), resume(). Das Skript laedt nur mit Ticket bzw. laufender Bearbeitung (Lader in public/index.html);
 *    Besucher bekommen nur das Adapter-Objekt am Konfigurations-Objekt, sonst nichts.
 * 4. `ed(key)` setzt die Kennzeichen (`data-edit`, `data-edit-mode`, `data-edit-label`) an ein Element.
 */
import { useEffect } from 'react';
import axios from 'axios';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { AGORA_PUBLIC_BASE, agoraCampusSlug, isEnglish, textParagraphs } from './publicContent';
import { getEditState, updateEditState, useEditState } from './editStore';
import {
  ABOUT_PREFIX, ABOUT_US_FIELDS, ABOUT_US_SCHEMA, PAGE_BY_PATH, WEBSITE_TEXTS,
  aboutFieldKind, labelForKey, websiteTextEntry,
} from './editKeys';

const NS = 'translation';
const LANGS = ['de', 'en'];
const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);
const filled = value => (Array.isArray(value)
  ? value.some(entry => typeof entry === 'string' && entry.trim())
  : typeof value === 'string' && value.trim() !== '');
const asString = value => (Array.isArray(value) ? value.join('\n\n') : (typeof value === 'string' ? value : ''));

/* ------------------------------------------------------------------ Website-Texte (i18next) */

// published: veroeffentlichte Eintraege (Public API); drafts: Entwurf aus dem Bearbeitungsmodus (null = eingebaut);
// originals: eingebaute Texte je Sprache, gemerkt bevor sie zum ersten Mal ueberschrieben werden.
const textState = { published: {}, drafts: {}, originals: { de: {}, en: {} } };

/** Eingebauter Text (translation.json) einer Kennung in einer Sprache; undefined, wenn es ihn nicht gibt. */
export function builtinText(i18n, lng, key) {
  const originals = textState.originals[lng] || {};
  if (has(originals, key)) return originals[key];
  return i18n && i18n.getResource ? i18n.getResource(lng, NS, key) : undefined;
}

/** Text, der gerade gelten soll: Entwurf vor veroeffentlicht; null = eingebauter Text dieser Sprache. */
function overrideFor(key, lng) {
  if (has(textState.drafts, key)) {
    const value = textState.drafts[key] && textState.drafts[key][lng];
    return filled(value) ? value : null;
  }
  const value = textState.published[key] && textState.published[key][lng];
  return filled(value) ? value : null;
}

function setNested(target, key, value) {
  const parts = key.split('.');
  let node = target;
  parts.slice(0, -1).forEach(part => {
    if (!node[part] || typeof node[part] !== 'object') node[part] = {};
    node = node[part];
  });
  node[parts[parts.length - 1]] = value;
}

/**
 * Spielt Overrides (bzw. den eingebauten Text, wenn kein Override gilt) fuer die geladenen Sprachen ein. Ein
 * `addResourceBundle` je Sprache (tiefes Zusammenfuehren) -> Ereignis 'added' -> react-i18next zeichnet neu.
 */
export function applyTexts(i18n = i18next, keys = null) {
  if (!i18n || !i18n.addResourceBundle) return;
  const list = keys || Array.from(new Set([...Object.keys(textState.published), ...Object.keys(textState.drafts)]));
  LANGS.forEach(lng => {
    if (i18n.hasResourceBundle && !i18n.hasResourceBundle(lng, NS)) return;
    const bundle = {};
    let count = 0;
    list.forEach(key => {
      if (!websiteTextEntry(key)) return;
      const originals = textState.originals[lng];
      if (!has(originals, key)) originals[key] = i18n.getResource(lng, NS, key);
      const builtin = originals[key];
      if (typeof builtin !== 'string') return;          // nur vorhandene Text-Schluessel, keine Teilbaeume
      const value = overrideFor(key, lng);
      setNested(bundle, key, value != null ? value : builtin);
      count += 1;
    });
    if (count) i18n.addResourceBundle(lng, NS, bundle, true, true);
  });
}

/** Veroeffentlichte Eintraege uebernehmen (nur Freigabeliste, nur Text) und anwenden. */
export function setPublishedTexts(entries, i18n = i18next) {
  const clean = {};
  Object.entries(entries && typeof entries === 'object' ? entries : {}).forEach(([key, entry]) => {
    if (!websiteTextEntry(key) || !entry || typeof entry !== 'object') return;
    const value = {};
    LANGS.forEach(lng => { if (filled(entry[lng])) value[lng] = String(entry[lng]); });
    if (Object.keys(value).length) clean[key] = value;
  });
  const previous = Object.keys(textState.published);
  textState.published = clean;
  applyTexts(i18n, Array.from(new Set([...previous, ...Object.keys(clean)])));
  return clean;
}

/** Entwurf eines Website-Texts (Bearbeitungsmodus); `null` je Sprache = eingebauter Text. */
export function setDraftText(key, value, i18n = i18next) {
  if (!websiteTextEntry(key)) return false;
  const source = value && typeof value === 'object' ? value : {};
  textState.drafts[key] = {
    de: filled(source.de) ? String(source.de) : null,
    en: filled(source.en) ? String(source.en) : null,
  };
  applyTexts(i18n, [key]);
  return true;
}

export function fetchPublishedTexts() {
  return agoraCampusSlug()
    .then(campus => axios.get(`${AGORA_PUBLIC_BASE}/content/website_texts`, { params: campus ? { campus } : {} }))
    .then(res => (res && res.data && res.data.entries && typeof res.data.entries === 'object' ? res.data.entries : {}))
    .catch(() => ({}));   // 404 = noch nichts gepflegt -> eingebaute Texte
}

/* ------------------------------------------------------------------ Ueber uns (zentrale Wahrheit) */

function flattenAbout(schema, node, prefix, out, lists) {
  if (Array.isArray(schema)) {
    if (!Array.isArray(node)) return;
    const positions = [];
    node.forEach((entry, index) => {
      if (!entry || typeof entry !== 'object') return;
      const position = Number.isInteger(entry.position) ? entry.position : index;
      positions.push(position);
      flattenAbout(schema[0], entry, `${prefix}.${position}`, out, lists);
    });
    lists[prefix] = positions;
  } else if (schema && typeof schema === 'object') {
    if (!node || typeof node !== 'object') return;
    Object.keys(schema).forEach(name => flattenAbout(schema[name], node[name], prefix ? `${prefix}.${name}` : name, out, lists));
  } else if (schema === 'paras') {
    if (Array.isArray(node)) out[prefix] = node.filter(entry => typeof entry === 'string');
  } else if (typeof node === 'string') {
    out[prefix] = node;
  }
}

/** Public-API-Antwort von `about_us` -> {data: {de: {pfad: wert}}, lists: {de: {vision_cards: [position]}}}. */
export function flattenAboutUs(content) {
  const locales = content && typeof content.locales === 'object' && content.locales ? content.locales : {};
  const data = {};
  const lists = {};
  LANGS.forEach(lng => {
    data[lng] = {};
    lists[lng] = {};
    flattenAbout(ABOUT_US_SCHEMA, locales[lng], '', data[lng], lists[lng]);
  });
  return { data, lists, raw: locales };
}

let aboutPromise = null;
export function loadAboutUs() {
  if (!aboutPromise) {
    aboutPromise = agoraCampusSlug()
      .then(campus => axios.get(`${AGORA_PUBLIC_BASE}/content/about_us`, { params: campus ? { campus } : {} }))
      .then(res => {
        const { data, lists, raw } = flattenAboutUs(res && res.data);
        updateEditState({ aboutData: data, aboutLists: lists, aboutRaw: raw });
      })
      .catch(() => { aboutPromise = null; updateEditState({ aboutData: { de: {}, en: {} } }); });
  }
  return aboutPromise;
}

/** Eingebauter Rueckfall eines Ueber-uns-Pfads (translation.json -> about_us_builtin.<pfad>). */
export function aboutBuiltin(i18n, lng, path) {
  const value = i18n && i18n.getResource ? i18n.getResource(lng, NS, `about_us_builtin.${path}`) : undefined;
  return typeof value === 'string' || Array.isArray(value) ? value : '';
}

/** Wert eines Ueber-uns-Pfads: Entwurf > AgoraHub (Public API) > eingebauter Text dieser Sprache. */
export function aboutValue(state, lng, path, builtin) {
  const override = state.aboutOverrides[path];
  if (override && has(override, lng)) return filled(override[lng]) ? override[lng] : builtin;
  const published = state.aboutData && state.aboutData[lng] && state.aboutData[lng][path];
  return filled(published) ? published : builtin;
}

/** Stellen einer Liste (vision_cards, values, logo_points): Seitensprache, sonst andere Sprache, sonst eingebaut. */
export function aboutListPositions(state, lng, name, builtinCount) {
  const other = lng === 'en' ? 'de' : 'en';
  const own = state.aboutLists && state.aboutLists[lng] && state.aboutLists[lng][name];
  if (own && own.length) return own;
  const fallback = state.aboutLists && state.aboutLists[other] && state.aboutLists[other][name];
  if (fallback && fallback.length) return fallback;
  return Array.from({ length: builtinCount }, (_, i) => i);
}

const dig = (node, path) => String(path).split('.').reduce((value, part) => (value == null ? undefined : value[part]), node);

/**
 * Link aus `about_us` (z. B. `leadership.global.pastor.cta`): Links bleiben in AgoraHub (nicht im Bearbeitungsmodus);
 * Seitensprache, sonst Deutsch, sonst eingebaut (`about_us_builtin`). Nur https.
 */
export function aboutLink(state, i18n, lng, path) {
  const pick = field => {
    const candidates = [dig(state.aboutRaw && state.aboutRaw[lng], `${path}.${field}`),
      dig(state.aboutRaw && state.aboutRaw.de, `${path}.${field}`), aboutBuiltin(i18n, lng, `${path}.${field}`)];
    return candidates.find(value => typeof value === 'string' && value.trim() && (field !== 'url' || value.startsWith('https://')));
  };
  return { label: pick('label') || '', url: pick('url') || '' };
}

/**
 * Ueber-uns-Texte fuer die aktuelle Sprache. `text(pfad)` liefert Text, `paras(pfad)` Absaetze (Liste oder Text mit
 * Leerzeilen), `list(name)` die Stellen einer Liste, `link(pfad)` {label, url}. Laedt `about_us` einmal je Seitenaufruf.
 */
export function useAboutUs() {
  const { i18n } = useTranslation();
  const aboutData = useEditState(s => s.aboutData);
  useEditState(s => s.aboutOverrides);
  useEditState(s => s.aboutLists);
  useEffect(() => { loadAboutUs(); }, []);
  const lng = isEnglish(i18n.language) ? 'en' : 'de';
  const state = getEditState();
  const value = path => aboutValue(state, lng, path, aboutBuiltin(i18n, lng, path));
  return {
    ready: aboutData !== null,
    text: path => asString(value(path)),
    paras: path => textParagraphs(value(path)),
    link: path => aboutLink(state, i18n, lng, path),
    list: name => {
      const builtin = i18n.getResource ? i18n.getResource(lng, NS, `about_us_builtin.${name}`) : null;
      return aboutListPositions(state, lng, name, Array.isArray(builtin) ? builtin.length : 0);
    },
  };
}

/** Entwurf eines Ueber-uns-Felds (Bearbeitungsmodus); Absatz-Felder als Liste, sonst Text, null = eingebaut. */
export function setDraftAbout(path, value) {
  const kind = aboutFieldKind(path);
  if (!kind) return false;
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const clean = raw => {
    if (raw == null) return null;
    if (kind === 'paras') return Array.isArray(raw) ? raw.map(String) : textParagraphs(raw);
    return Array.isArray(raw) ? raw.join('\n\n') : String(raw);
  };
  updateEditState(s => ({ aboutOverrides: { ...s.aboutOverrides, [path]: { de: clean(source.de), en: clean(source.en) } } }));
  return true;
}

/* ------------------------------------------------------------------ Kennzeichen + Hooks fuer die Seiten */

/** Kennzeichen fuer ein bearbeitbares Element: `<h1 {...ed('home.title')}>`. Modus: text | lines | paragraphs. */
export function ed(key, mode, label) {
  const props = { 'data-edit': key };
  if (mode && mode !== 'text') props['data-edit-mode'] = mode;
  const text = label || labelForKey(key);
  if (text) props['data-edit-label'] = text;
  return props;
}

/** Ueber-uns-Kennung `about_us:<pfad>` mit Beschriftung. */
export const edAbout = (path, label) => ed(`${ABOUT_PREFIX}${path}`, undefined, label);

/** Bearbeitungsmodus aktiv (Slideshows anhalten, aufklappbare Texte offen zeigen). */
export const useEditing = () => useEditState(s => s.editing);

/** Seitentitel + Suchmaschinen-Beschreibung (Kennungen meta.<seite>.title / .description, meta.site_name). */
export function usePageMeta(page) {
  const { t } = useTranslation();
  const title = t(`meta.${page}.title`);
  const site = t('meta.site_name');
  const description = t(`meta.${page}.description`);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.title = page === 'home' || !title ? (title || site) : `${title} | ${site}`;
    let tag = document.querySelector('meta[name="description"]');
    if (!tag && description) {
      tag = document.createElement('meta');
      tag.setAttribute('name', 'description');
      document.head.appendChild(tag);
    }
    if (tag && description) tag.setAttribute('content', description);
  }, [page, title, site, description]);
}

/* ------------------------------------------------------------------ Adapter fuer das Bearbeitungs-Skript */

export const pageForPath = pathname => PAGE_BY_PATH[String(pathname || '/').replace(/\/+$/, '') || '/'] || 'home';

function listKeys(i18n, key) {
  const builtin = builtinText(i18n, 'de', key) || builtinText(i18n, 'en', key);
  const length = Array.isArray(builtin) ? builtin.length : 0;
  return Array.from({ length }, (_, i) => `${key}.${i}`);
}

/** Alle bearbeitbaren Texte einer Seite (auch unsichtbare) mit Beschriftung und eingebauten Texten (de + en). */
export function pageTexts(page, i18n = i18next) {
  const items = [];
  const entries = WEBSITE_TEXTS.filter(entry => entry.page === page)
    .concat(WEBSITE_TEXTS.filter(entry => entry.page === 'shared'));
  entries.forEach(entry => {
    (entry.list ? listKeys(i18n, entry.key) : [entry.key]).forEach(key => {
      const item = { key, label: labelForKey(key), defaults: {} };
      if (entry.mode) item.mode = entry.mode;
      LANGS.forEach(lng => { item.defaults[lng] = asString(builtinText(i18n, lng, key)); });
      items.push(item);
    });
  });
  if (page === 'about') {
    const state = getEditState();
    ABOUT_US_FIELDS.forEach(field => {
      let paths = [field.path];
      if (field.list) {
        const builtin = i18n.getResource ? i18n.getResource('de', NS, `about_us_builtin.${field.list}`) : null;
        paths = aboutListPositions(state, 'de', field.list, Array.isArray(builtin) ? builtin.length : 0)
          .map(position => field.path.replace('#', String(position)));
      }
      paths.forEach(path => {
        const key = `${ABOUT_PREFIX}${path}`;
        const item = { key, label: labelForKey(key), defaults: {} };
        if (aboutFieldKind(path) === 'paras') item.mode = 'paragraphs';
        LANGS.forEach(lng => { item.defaults[lng] = asString(aboutBuiltin(i18n, lng, path)); });
        items.push(item);
      });
    });
  }
  return items;
}

export function createEditAdapter(i18n = i18next, win = typeof window !== 'undefined' ? window : null) {
  return {
    texts() {
      return pageTexts(pageForPath(win && win.location ? win.location.pathname : '/'), i18n);
    },
    setText(key, value) {
      if (typeof key === 'string' && key.startsWith(ABOUT_PREFIX)) return setDraftAbout(key.slice(ABOUT_PREFIX.length), value);
      return setDraftText(key, value, i18n);
    },
    setSlot(slotId, images) {
      if (typeof slotId !== 'string' || !slotId) return;
      const list = (Array.isArray(images) ? images : []).filter(im => im && typeof im.url === 'string' && im.url.startsWith('https://'));
      updateEditState(s => ({ slots: { ...s.slots, [slotId]: list } }));
    },
    pause() {
      updateEditState({ editing: true });
      // Fuer die eingebauten Texte im Bearbeitungsfeld werden beide Sprachen gebraucht (Besucher laden nur eine).
      if (i18n && i18n.loadLanguages) i18n.loadLanguages(LANGS);
      warnOnCampusMismatch(win);
    },
    resume() {
      updateEditState({ editing: false });
    },
  };
}

function warnOnCampusMismatch(win) {
  const configured = win && win.AGORA_EDIT && win.AGORA_EDIT.campus;
  agoraCampusSlug().then(campus => {
    if (configured !== undefined && campus && campus !== configured && win.console) {
      win.console.warn(`AgoraEdit: Standort im Lader (${configured || 'leer'}) weicht vom Standort der Website (${campus}) ab.`);
    }
  });
}

/** Adapter anmelden: beim laufenden Skript direkt, sonst am Konfigurations-Objekt fuer den spaeteren Start. */
export function registerEditAdapter(win = typeof window !== 'undefined' ? window : null, adapter = createEditAdapter(i18next, win)) {
  if (!win) return null;
  if (win.AgoraEdit && typeof win.AgoraEdit.register === 'function') {
    win.AgoraEdit.register(adapter);
    return 'register';
  }
  if (win.AGORA_EDIT && typeof win.AGORA_EDIT === 'object') {
    win.AGORA_EDIT.adapter = adapter;
    return 'config';
  }
  return null;
}

let started = false;
/** Einmal beim Start (src/index.js): veroeffentlichte Texte laden/anwenden, nachgeladene Sprachen nachziehen, Adapter. */
export function initEditTexts({ i18n = i18next, win = typeof window !== 'undefined' ? window : null, fetchTexts = fetchPublishedTexts } = {}) {
  if (started) return;
  started = true;
  if (i18n && i18n.on) i18n.on('loaded', () => applyTexts(i18n));
  fetchTexts().then(entries => setPublishedTexts(entries, i18n));
  registerEditAdapter(win, createEditAdapter(i18n, win));
}

/** Nur fuer Tests: interner Zustand zurueck auf Anfang. */
export function _resetEditTexts() {
  textState.published = {};
  textState.drafts = {};
  textState.originals = { de: {}, en: {} };
  aboutPromise = null;
  started = false;
}
